/*
# -----------------------------------------------------------------------------
#	Project: HSE Genius®
#	Client ID: 834215
#	Registration Code: IT-2025-ZR319-MQ744
#	Author: KaimakiWeb
#	Website: https://kaimakiweb.com
#	Copyright (c) 2024-2026 Kaimaki Web. All rights reserved.
#
#	This software and its source code are the property of KaimakiWeb.
#	Unauthorized copying, modification, distribution, or use of this software,
#	via any medium, is strictly prohibited unless prior written permission
#	is obtained from KaimakiWeb.
# -----------------------------------------------------------------------------
*/

import ApiService, { getApiBaseUrl } from "../api-service";
import axios from "axios";
import { setupToastErrors } from "../apiToastError";
import { attachLanguageHeader } from "../interceptors";

class UserApiService extends ApiService {
  constructor(prefix) {
    super(prefix);

    this.axiosInstance = axios.create({ baseURL: getApiBaseUrl() });
    attachLanguageHeader(this.axiosInstance);
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      setupToastErrors
    );
  }

  createUser = async (userData) => {
    return await this.axiosInstance
      .post("/user/create/", userData)
      .then((res) => res.data);
  };

  createConsultant = async (userData) => {
    return await this.axiosInstance
      .post("/user/consultant/create/", userData)
      .then((res) => res.data);
  };

  resendActivation = async (email) => {
    // Send the request directly without going through the interceptors
    return await this.axiosInstance
      .post("/user/activate/resend-token/", { email })
      .then((res) => res.data);
  };

  activateAccount = async (data) => {
    // Send the request directly without going through the interceptors
    return await this.axiosInstance
      .post("/user/profile/activate/", data)
      .then((res) => res.data);
  };

  requestPasswordReset = async (email) => {
    return await this.axiosInstance
      .post("/user/reset-password/", { email })
      .then((res) => res.data);
  };

  confirmPasswordReset = async ({ token, new_password }) => {
    return await this.axiosInstance
      .post("/user/reset-password/confirm/", { token, new_password })
      .then((res) => res.data);
  };

  acceptInvite = async (data) => {
    if (!data.token || !data.password) {
      console.error("API: Missing required invite parameters");
      throw new Error("Missing required invite parameters");
    }

    // Create a direct axios instance with no interceptors to bypass authentication entirely.
    const axiosInstance = attachLanguageHeader(
      axios.create({
        baseURL: getApiBaseUrl(),
        headers: {
          "Content-Type": "application/json",
        },
      })
    );

    // Send the request directly without going through the interceptors
    const response = await axiosInstance.post("/user/invite/accept/", data);
    return response.data;
  };

  getUserProfile = async () => {
    // Let errors propagate to the caller; the saga that invokes this already
    // handles failures by dispatching profileData(null).
    const data = await this.get("/profile").then((res) => res.data);
    return data;
  };
}

export const UserApi = new UserApiService("user");
